// episodes.js — pure logic for the Ongoing Symptom Episode feature.
// Computes timeline metadata, summary statistics, and the VA-evidence narrative
// from an episode header + its day-by-day symptom entries. No React, no
// network — fully Jest-testable and shared by the active-card UI, the end-of-
// episode summary modal, and the Symptom Evidence PDF.

import { fmtHours, parseDurationToHours } from "./symptomDiary";

const MS_PER_DAY = 24 * 3600 * 1000;

function toDate(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function fmtDate(d) {
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** Inclusive day-count between two timestamps (calendar days, local time). */
export function inclusiveDayCount(start, end) {
  const s = toDate(start);
  const e = toDate(end);
  if (!s || !e) return 0;
  const sMid = new Date(s.getFullYear(), s.getMonth(), s.getDate());
  const eMid = new Date(e.getFullYear(), e.getMonth(), e.getDate());
  return Math.max(1, Math.round((eMid - sMid) / MS_PER_DAY) + 1);
}

/** Current duration of an active episode in calendar days (1-based). */
export function activeEpisodeDayCount(episode, now = new Date()) {
  return inclusiveDayCount(episode?.startDate, now);
}

function dedupe(list) {
  return Array.from(new Set((list || []).filter(Boolean)));
}

function uniqMeds(days) {
  const seen = new Set();
  const out = [];
  days.forEach((d) => (d.medsTaken || []).forEach((m) => {
    const name = String(m?.name || "").trim();
    if (!name) return;
    const key = name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(name);
  }));
  return out;
}

/**
 * Build the summary statistics for an episode (active or ended).
 * Returns { startDate, endDate, totalDays, dayCount, worstSeverity,
 *           worstSeverityDate, latestSeverity, avgSeverity, symptoms,
 *           medications, treatments, triggers, impact, missedActivities,
 *           missedDaysTotal, notes }.
 */
export function buildEpisodeSummary(episode, days = []) {
  const ep = episode || {};
  const sorted = [...days].filter((d) => d?.date).sort((a, b) => new Date(a.date) - new Date(b.date));
  const severities = sorted.map((d) => Number(d.severity || 0));
  const worst = severities.length ? Math.max(...severities) : 0;
  const worstIdx = severities.findIndex((s) => s === worst);
  const avg = severities.length ? severities.reduce((a, b) => a + b, 0) / severities.length : 0;

  const startDate = ep.startDate || sorted[0]?.date || "";
  const effectiveEnd = ep.endDate || sorted[sorted.length - 1]?.date || "";
  const totalDays = startDate && effectiveEnd ? inclusiveDayCount(startDate, effectiveEnd) : 0;

  return {
    id: ep.id || "",
    status: ep.status || "active",
    symptom: ep.symptom || sorted[0]?.symptom || "",
    bodySide: ep.bodySide || "",
    bodyRegions: ep.bodyRegions || [],
    linkedIssueId: ep.linkedIssueId || "",
    suspectedCondition: ep.suspectedCondition || "",
    isUndiagnosed: !!ep.isUndiagnosed,
    startDate,
    endDate: ep.endDate || "",
    totalDays,
    dayCount: sorted.length,
    latestSeverity: severities.length ? severities[severities.length - 1] : 0,
    worstSeverity: worst,
    worstSeverityDate: worstIdx >= 0 ? sorted[worstIdx].date : "",
    avgSeverity: Number.isFinite(avg) ? Math.round(avg * 10) / 10 : 0,
    symptoms: dedupe(sorted.flatMap((d) => [d.symptom].concat(d.bodyRegions || []))),
    medications: uniqMeds(sorted),
    treatments: dedupe(sorted.flatMap((d) => d.treatments || [])),
    triggers: dedupe(sorted.flatMap((d) => d.triggers || [])),
    impact: dedupe(sorted.flatMap((d) => d.impact || [])),
    missedActivities: dedupe(sorted.flatMap((d) => d.missedActivities || [])),
    missedDaysTotal: sorted.reduce((a, d) => a + (Number(d.missedDays) || 0), 0),
    closingNotes: ep.closingNotes || "",
  };
}

/**
 * Render the C&P-friendly narrative paragraph from a summary.
 * Example: "From June 13 to June 17, the veteran logged a 5-day gout flare
 * affecting the left foot. Severity ranged from 6/10 to 9/10, with worst
 * severity on June 15. The veteran used indomethacin and rest. Functional
 * impact included difficulty walking, standing, and completing normal daily
 * activities."
 */
export function buildEpisodeNarrative(summary) {
  if (!summary) return "";
  const sd = toDate(summary.startDate);
  const ed = toDate(summary.endDate) || toDate(summary.worstSeverityDate);
  const condition = (summary.symptom || "ongoing condition").trim();
  const sideClause = summary.bodySide && summary.bodySide !== "N/A"
    ? ` affecting the ${summary.bodySide.toLowerCase()} side`
    : "";
  const bodyClause = summary.bodyRegions?.length
    ? ` (${summary.bodyRegions.join(", ").toLowerCase()})`
    : "";
  const dateClause = sd && ed
    ? `From ${fmtDate(sd)} to ${fmtDate(ed)}`
    : sd
      ? `Starting ${fmtDate(sd)}`
      : "Across this episode";
  const daysClause = summary.totalDays
    ? `, the veteran logged a ${summary.totalDays}-day ${condition.toLowerCase()} episode${sideClause}${bodyClause}.`
    : `, the veteran logged an ongoing ${condition.toLowerCase()} episode${sideClause}${bodyClause}.`;

  const sevLow = summary.dayCount
    ? Math.min(...[summary.worstSeverity, summary.latestSeverity, Math.floor(summary.avgSeverity)].filter((n) => n > 0))
    : 0;
  const sevHigh = summary.worstSeverity;
  const worstDate = toDate(summary.worstSeverityDate);
  const severityClause = sevHigh
    ? ` Severity ranged from ${sevLow || sevHigh}/10 to ${sevHigh}/10${worstDate ? `, with worst severity on ${fmtDate(worstDate)}` : ""}.`
    : "";

  const treatments = [...summary.medications, ...summary.treatments];
  const treatmentClause = treatments.length
    ? ` The veteran used ${treatments.slice(0, 6).join(", ")}.`
    : "";

  const impactList = [...summary.impact, ...summary.missedActivities.map((m) => `missed ${m.toLowerCase()}`)];
  const impactClause = impactList.length
    ? ` Functional impact included ${impactList.slice(0, 8).join(", ").toLowerCase()}.`
    : "";

  const missedClause = summary.missedDaysTotal
    ? ` Total missed work/PT/duty: ${summary.missedDaysTotal} day${summary.missedDaysTotal === 1 ? "" : "s"}.`
    : "";

  return `${dateClause}${daysClause}${severityClause}${treatmentClause}${impactClause}${missedClause}`.trim();
}

/** Pick which previous-day fields to carry forward into a fresh daily update. */
export function buildCarryForward(prevDay) {
  if (!prevDay) {
    return {
      severity: 5,
      triggers: [],
      medsTaken: [],
      treatments: [],
      impact: [],
      missedActivities: [],
      treatmentHelped: "",
      factors: {},
      conditionFields: {},
    };
  }
  return {
    severity: prevDay.severity ?? 5,
    triggers: [...(prevDay.triggers || [])],
    medsTaken: (prevDay.medsTaken || []).map((m) => ({ ...m })),
    treatments: [...(prevDay.treatments || [])],
    impact: [...(prevDay.impact || [])],
    missedActivities: [...(prevDay.missedActivities || [])],
    treatmentHelped: prevDay.treatmentHelped || "",
    factors: { ...(prevDay.factors || {}) },
    conditionFields: { ...(prevDay.conditionFields || {}) },
  };
}

/** Apply a trend ("better"/"same"/"worse") to a carry-forward severity. */
export function severityFromTrend(prevSeverity, trend) {
  const cur = Number(prevSeverity ?? 5);
  if (trend === "better") return Math.max(0, cur - 2);
  if (trend === "worse") return Math.min(10, cur + 2);
  if (trend === "same") return cur;
  return cur;
}

/** Relative time label e.g. "2h ago", "3d ago". */
export function relativeTime(iso, now = new Date()) {
  const d = toDate(iso);
  if (!d) return "";
  const diff = Math.max(0, now.getTime() - d.getTime());
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/** Format the day's date label for the timeline (e.g. "Mon Jun 14"). */
export function fmtDayLabel(iso) {
  const d = toDate(iso);
  if (!d) return "";
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

/** Read the trend tag out of a day's tags[] (set by the backend on daily update). */
export function dayTrend(day) {
  const tag = (day?.tags || []).find((t) => t.startsWith("trend:"));
  return tag ? tag.slice(6) : "";
}

/** Compute approximate total hours when needed (uses duration text per day if present). */
export function approxEpisodeHours(days = []) {
  return days.reduce((acc, d) => acc + (parseDurationToHours(d.duration) || 0), 0);
}

export function fmtEpisodeDuration(hours) {
  return fmtHours(hours);
}
